import { useCallback, useMemo, useRef, useState } from 'react';

import { Toast, useToast } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';

type UseBatchMutationToastOptions = {
  successMessage: string;
  debounceMs?: number;
};

type UseBatchMutationToastReturn = {
  startRequest: () => void;
  handleSuccess: () => void;
  handleError: () => void;
};

export const useBatchMutationToast = ({
  successMessage,
  debounceMs = 500,
}: UseBatchMutationToastOptions): UseBatchMutationToastReturn => {
  const { enqueueToast } = useToast();
  const [inFlightCount, setInFlightCount] = useState(0);
  const hasErrorInBatch = useRef(false);

  const debouncedSuccessToast = useMemo(
    () =>
      debounce(() => {
        enqueueToast(<Toast tone="success">{successMessage}</Toast>);
      }, debounceMs),
    [enqueueToast, successMessage, debounceMs],
  );

  const startRequest = useCallback(() => {
    setInFlightCount((prev) => prev + 1);
  }, []);

  const handleSuccess = useCallback(() => {
    setInFlightCount((prev) => {
      const newCount = prev - 1;
      if (newCount === 0) {
        if (!hasErrorInBatch.current) {
          debouncedSuccessToast();
        }
        hasErrorInBatch.current = false;
      }
      return newCount;
    });
  }, [debouncedSuccessToast]);

  const handleError = useCallback(() => {
    hasErrorInBatch.current = true;
    setInFlightCount((prev) => {
      const newCount = prev - 1;
      if (newCount === 0) {
        hasErrorInBatch.current = false;
      }
      return newCount;
    });
  }, []);

  return {
    startRequest,
    handleSuccess,
    handleError,
  };
};
